import React, { useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import MainContentWrapper from "../../Components/MainContentWrapper";
import Popup from "../../ReusableFunctions/Popup";
import { Button } from "../../Components/Helper";
import DownloadIcon from "../../assets/Download.png";
import calender from "../../assets/blueCalendar.svg";
import { downloadPilotsPerformanceReportExcel, getPilotsPerformanceReport } from "../../apis/reports";
import TableView from "../../ReusableComponents/TableView/TableView";

const periodOptions = [
  { key: "day", label: "Today" },
  { key: "week", label: "This Week" },
  { key: "month", label: "This Month" },
  { key: "year", label: "This Year" },
  { key: "custom", label: "Custom" },
];

const startEndForPeriod = (periodKey) => {
  const now = new Date();
  const todayEnd = new Date(now);
  todayEnd.setHours(23, 59, 59, 999);
  let fromDate = new Date();
  let toDate = new Date();

  switch (periodKey) {
    case "day":
      fromDate.setHours(0, 0, 0, 0);
      toDate.setHours(23, 59, 59, 999);
      break;
    case "week": {
      const day = now.getDay(); // 0 Sunday - 6 Saturday
      fromDate = new Date(now);
      fromDate.setDate(now.getDate() - (day === 0 ? 6 : day - 1)); // Monday
      fromDate.setHours(0, 0, 0, 0);
      toDate = new Date(fromDate);
      toDate.setDate(fromDate.getDate() + 6); // Sunday
      toDate.setHours(23, 59, 59, 999);
      break;
    }
    case "month":
      fromDate = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
      toDate = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
      break;
    case "year":
      fromDate = new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
      toDate = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999);
      break;
    default:
      fromDate.setHours(0, 0, 0, 0);
      toDate.setHours(23, 59, 59, 999);
      break;
  }

  // Disallow future dates: clamp end date to today (end of day)
  if (toDate.getTime() > todayEnd.getTime()) toDate = todayEnd;

  return { fromDate, toDate };
};

const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

const PilotsPerformanceReport = () => {
  const initial = useMemo(() => startEndForPeriod("day"), []);
  const [period, setPeriod] = useState("day");
  const [lastPresetPeriod, setLastPresetPeriod] = useState("day");
  const [fromDate, setFromDate] = useState(initial.fromDate);
  const [toDate, setToDate] = useState(initial.toDate);
  const today = useMemo(() => new Date(), []);
  const [openCustomPopover, setOpenCustomPopover] = useState(false);
  const [customFrom, setCustomFrom] = useState(initial.fromDate);
  const [customTo, setCustomTo] = useState(initial.toDate);
  const [openCustomDatePicker, setOpenCustomDatePicker] = useState({ from: false, to: false });

  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [tableState, setTableState] = useState({
    pageSize: 10,
    pageNumber: 1,
    orderBy: "pilot_name",
    orderType: "ASC",
  });
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const fetchReport = async ({ from, to, pageNumber, pageSize, orderBy, orderType }) => {
    try {
      setLoading(true);
      const data = await getPilotsPerformanceReport({
        fromDate: from.toISOString(),
        toDate: to.toISOString(),
        pageNumber,
        pageSize,
        orderBy,
        orderType,
      });
      const normalized = (data?.rows || []).map((r) => ({
        ...r,
        completed: Number(r?.completed || 0),
        cancelled: Number(r?.cancelled || 0),
        missed: Number(r?.missed || 0),
        paid_requests: Number(r?.paid_requests || 0),
        acres_completed: Number(r?.acres_completed || 0),
        revenue_generated: Number(r?.revenue_generated || 0),
      }));
      setRows(normalized);
      setCount(Number(data?.count || 0));
      setLoading(false);
    } catch (e) {
      setLoading(false);
      Popup.alert("Error", e?.message || "Failed to load report.", "ERROR");
    }
  };

  useEffect(() => {
    if (period === "custom") return;
    setLastPresetPeriod(period);
    const range = startEndForPeriod(period);
    setFromDate(range.fromDate);
    setToDate(range.toDate);
    setTableState((prev) => ({ ...prev, pageNumber: 1 }));
    fetchReport({ from: range.fromDate, to: range.toDate, ...tableState, pageNumber: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period]);

  useEffect(() => {
    // initial load
    fetchReport({ from: initial.fromDate, to: initial.toDate, ...tableState });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDownload = async () => {
    try {
      setDownloading(true);
      const blob = await downloadPilotsPerformanceReportExcel({
        fromDate: fromDate.toISOString(),
        toDate: toDate.toISOString(),
        orderBy: tableState.orderBy,
        orderType: tableState.orderType,
      });
      const safeFrom = fromDate.toISOString().slice(0, 10);
      const safeTo = toDate.toISOString().slice(0, 10);
      downloadBlob(blob, `pilots_performance_${safeFrom}_to_${safeTo}.xlsx`);
      setDownloading(false);
    } catch (e) {
      setDownloading(false);
      Popup.alert("Error", e?.message || "Failed to download report.", "ERROR");
    }
  };

  const openCustom = () => {
    setCustomFrom(fromDate);
    setCustomTo(toDate);
    setOpenCustomPopover(true);
  };

  const closeCustom = () => {
    setOpenCustomPopover(false);
    setOpenCustomDatePicker({ from: false, to: false });
  };

  const applyCustom = () => {
    if (!customFrom || !customTo) return;
    if (customFrom.getTime() > customTo.getTime()) {
      return Popup.alert("Reports", "From date cannot be after To date.", "ERROR");
    }
    setFromDate(customFrom);
    setToDate(customTo);
    setPeriod("custom");
    setTableState((prev) => ({ ...prev, pageNumber: 1 }));
    fetchReport({ from: customFrom, to: customTo, ...tableState, pageNumber: 1 });
    setOpenCustomPopover(false);
  };

  const cancelCustom = () => {
    setOpenCustomPopover(false);
    setOpenCustomDatePicker({ from: false, to: false });
    // revert toggle to previous preset period
    setPeriod(lastPresetPeriod || "day");
  };

  return (
    <MainContentWrapper
      header={"Pilots Performance Report"}
      rightContent={
        <Button
          text={"Download Report"}
          // icon={DownloadIcon}
          onClick={onDownload}
          isLoading={downloading}
          LoadingText={"Downloading..."}
          style={{ height: "35px", width: "160px", fontSize: "14px", fontWeight: 500 }}
          isDisabled={loading || !fromDate || !toDate}
        />
      }
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: "12px",
          padding: "0px 0px 12px 0px",
          borderBottom: "1px solid #BFC8D9",
        }}
      >
        {/* Left: current applied date range */}
        <div style={{ fontSize: "12px", color: "#3B3B3B", fontWeight: 600 }}>
          {fromDate ? fromDate.toLocaleDateString("en-GB") : "--/--/----"} - {toDate ? toDate.toLocaleDateString("en-GB") : "--/--/----"}
        </div>

        {/* Right: range selector */}
        <div className="duration_selectors" style={{ fontSize: "14px", width: "auto" }}>
          {periodOptions.map((p, i) => {
            const isCustomBtn = p.key === "custom";
            const btn = (
              <button
                key={p.key}
                style={{
                  borderLeft: i === 0 && period !== p.key && "1px solid #C9C9C9",
                  borderRight: period !== p.key && "1px solid #C9C9C9",
                }}
                className={period === p.key ? "active" : ""}
                onClick={() => {
                  if (p.key === "custom") openCustom();
                  else {
                    closeCustom();
                    setPeriod(p.key);
                  }
                }}
              >
                {p.label}
              </button>
            );

            if (!isCustomBtn) return btn;

            return (
              <div key="custom-wrapper" style={{ position: "relative", display: "inline-block" }}>
                {btn}

                {openCustomPopover && (
                  <div
                    style={{
                      position: "absolute",
                      right: 0,
                      top: "42px",
                      zIndex: 1000,
                      background: "#FFFFFF",
                      border: "1px solid rgba(191, 200, 217, 0.8)",
                      borderRadius: "12px",
                      padding: "12px",
                      boxShadow: "0px 8px 24px rgba(0,0,0,0.12)",
                      minWidth: "420px",
                    }}
                  >
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                        <div style={{ fontSize: "12px", fontWeight: 600, color: "#3B3B3B" }}>From</div>
                        <div
                          className="input-date"
                          style={{
                            backgroundColor: "white",
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            position: "relative",
                            height: "35px",
                            border: "1.4px solid rgba(217, 217, 217, 0.84)",
                            borderRadius: "8px",
                            cursor: "pointer",
                          }}
                          onClick={() => setOpenCustomDatePicker((prev) => ({ ...prev, from: true, to: false }))}
                        >
                          <DatePicker
                            onClickOutside={() => setOpenCustomDatePicker((prev) => ({ ...prev, from: false }))}
                            open={openCustomDatePicker.from}
                            dateFormat="dd/MM/yyyy"
                            className="input-date-hidden"
                            selected={customFrom}
                            onSelect={() => setOpenCustomDatePicker((prev) => ({ ...prev, from: false }))}
                            onChange={(d) => {
                              if (!d) return;
                              const fd = new Date(d);
                              fd.setHours(0, 0, 0, 0);
                              setCustomFrom(fd);
                              if (customTo && fd.getTime() > customTo.getTime()) {
                                const td = new Date(fd);
                                td.setHours(23, 59, 59, 999);
                                setCustomTo(td);
                              }
                              setOpenCustomDatePicker((prev) => ({ ...prev, from: false }));
                            }}
                            maxDate={customTo ? (customTo.getTime() < today.getTime() ? customTo : today) : today}
                            readOnly
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            popperClassName="reports-datepicker-popper"
                            popperPlacement="bottom-start"
                          />
                          <img src={calender} alt="cal" style={{ width: "18px", position: "absolute", right: "12px" }} />
                        </div>
                      </div>

                      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                        <div style={{ fontSize: "12px", fontWeight: 600, color: "#3B3B3B" }}>To</div>
                        <div
                          className="input-date"
                          style={{
                            backgroundColor: "white",
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            position: "relative",
                            height: "35px",
                            border: "1.4px solid rgba(217, 217, 217, 0.84)",
                            borderRadius: "8px",
                            cursor: customFrom ? "pointer" : "not-allowed",
                            opacity: customFrom ? 1 : 0.6,
                          }}
                          onClick={() => {
                            if (!customFrom) return;
                            setOpenCustomDatePicker((prev) => ({ ...prev, to: true, from: false }));
                          }}
                        >
                          <DatePicker
                            onClickOutside={() => setOpenCustomDatePicker((prev) => ({ ...prev, to: false }))}
                            open={openCustomDatePicker.to}
                            dateFormat="dd/MM/yyyy"
                            className="input-date-hidden"
                            selected={customTo}
                            onSelect={() => setOpenCustomDatePicker((prev) => ({ ...prev, to: false }))}
                            onChange={(d) => {
                              if (!d) return;
                              const td = new Date(d);
                              td.setHours(23, 59, 59, 999);
                              setCustomTo(td);
                              if (customFrom && customFrom.getTime() > td.getTime()) {
                                const fd = new Date(td);
                                fd.setHours(0, 0, 0, 0);
                                setCustomFrom(fd);
                              }
                              setOpenCustomDatePicker((prev) => ({ ...prev, to: false }));
                            }}
                            minDate={customFrom || undefined}
                            maxDate={today}
                            disabled={!customFrom}
                            readOnly
                            showMonthDropdown
                            showYearDropdown
                            dropdownMode="select"
                            popperClassName="reports-datepicker-popper"
                            popperPlacement="bottom-start"
                          />
                          <img src={calender} alt="cal" style={{ width: "18px", position: "absolute", right: "12px" }} />
                        </div>
                      </div>
                    </div>

                    <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "12px" }}>
                      <Button
                        text={"Cancel"}
                        onClick={cancelCustom}
                        className={"btn-large lg-cancel-btn"}
                        isBtnNotActive={true}
                      />
                      <Button
                        text={"Apply"}
                        onClick={applyCustom}
                        className={"btn-large"}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <TableView
        showSrNo
        isLoading={loading}
        hidePagination={false}
        loadingHeight={"55vh"}
        tableContainerHeight={"681px"}
        pageSize={10}
        pageNumber={tableState.pageNumber}
        columns={[
          { Header: "Pilot", accessor: "pilot_name", width: "22%", isSortable: true },
          { Header: "Missed", accessor: "missed", width: "10%", isSortable: true, isTextCenter: true },
          { Header: "Cancelled", accessor: "cancelled", width: "10%", isSortable: true, isTextCenter: true },
          { Header: "Completed", accessor: "completed", width: "10%", isSortable: true, isTextCenter: true },
          { Header: "Paid Requests", accessor: "paid_requests", width: "12%", isSortable: true, isTextCenter: true },
          { Header: "Acres Completed", accessor: "acres_completed", width: "14%", isSortable: true, isTextCenter: true },
          { Header: "Revenue Generated", accessor: "revenue_generated", width: "18%", isSortable: true, isTextCenter: true },
        ]}
        data={rows}
        count={count}
        orderBy={tableState.orderBy}
        orderType={tableState.orderType}
        onStateChange={(state) => {
          const next = {
            pageSize: Number(state?.pageSize || 10),
            pageNumber: Number(state?.pageNumber || 1),
            orderBy: state?.orderBy || tableState.orderBy,
            orderType: state?.orderType || tableState.orderType,
          };
          setTableState(next);
          fetchReport({ from: fromDate, to: toDate, ...next });
        }}
      />
    </MainContentWrapper>
  );
};

export default PilotsPerformanceReport;

